/**
 * Request batching and deduplication utilities for generateStaticParams
 * Reduces concurrent Storyblok API calls during build time
 */

import { ISbStoryData } from 'storyblok-js-client';

import { getStories } from '@monoimin/storyblok/getStories';
import { GetStoriesParams } from '@monoimin/storyblok/types';

interface BatchRequest {
  params: GetStoriesParams;
  resolve: (data: any) => void;
  reject: (error: Error) => void;
}

class StaticParamsBatcher {
  private queue: BatchRequest[] = [];
  private processing = false;
  private readonly batchDelay = 200; // ms to wait for more requests
  private readonly maxBatchSize = 5;

  /**
   * Add a request to the batch queue
   */
  public async batchGetStories<T>(params: GetStoriesParams): Promise<{ data: { stories: ISbStoryData<T>[] } }> {
    return new Promise((resolve, reject) => {
      this.queue.push({ params, resolve, reject });
      
      if (!this.processing) {
        this.scheduleProcessing();
      }
    });
  }

  /**
   * Schedule batch processing with a delay to collect more requests
   */
  private scheduleProcessing(): void {
    this.processing = true;
    
    setTimeout(async () => {
      await this.processBatch();
      this.processing = false;
      
      // If there are more requests, schedule another batch
      if (this.queue.length > 0) {
        this.scheduleProcessing();
      }
    }, this.batchDelay);
  }

  /**
   * Process the current batch of requests
   */
  private async processBatch(): Promise<void> {
    if (this.queue.length === 0) return;
    
    const currentBatch = this.queue.splice(0, this.maxBatchSize);
    
    // Group requests by similar parameters to optimize caching
    const groupedRequests = this.groupSimilarRequests(currentBatch);
    
    for (const group of groupedRequests) {
      try {
        // Process each group sequentially to avoid rate limits
        await this.processGroup(group);
        
        // Small delay between groups
        if (groupedRequests.indexOf(group) < groupedRequests.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      } catch (error) {
        group.forEach(request => 
          request.reject(error instanceof Error ? error : new Error(String(error)))
        );
      }
    }
  }

  /**
   * Group similar requests together for better caching
   */
  private groupSimilarRequests(requests: BatchRequest[]): BatchRequest[][] {
    const groups: { [key: string]: BatchRequest[] } = {};
    
    requests.forEach(request => {
      // Create a key based on starts_with and content type for grouping
      const key = `${request.params.starts_with || ''}_${request.params.filter_query?.component?.in || ''}`;
      
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(request);
    });
    
    return Object.values(groups);
  }

  /**
   * Process a group of similar requests
   */
  private async processGroup(group: BatchRequest[]): Promise<void> {
    // If all requests in the group are identical, make one request
    if (this.areRequestsIdentical(group)) {
      const firstRequest = group[0];
      
      try {
        const result = await getStories(firstRequest.params);
        group.forEach(request => request.resolve(result));
      } catch (error) {
        group.forEach(request => 
          request.reject(error instanceof Error ? error : new Error(String(error)))
        );
      }
    } else {
      // Process requests sequentially within the group
      for (const request of group) {
        try {
          const result = await getStories(request.params);
          request.resolve(result);
          
          // Small delay between individual requests
          if (group.indexOf(request) < group.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 50));
          }
        } catch (error) {
          request.reject(error instanceof Error ? error : new Error(String(error)));
        }
      }
    }
  }

  /**
   * Check if all requests in a group are identical
   */
  private areRequestsIdentical(group: BatchRequest[]): boolean {
    if (group.length <= 1) return true;
    
    const firstParams = JSON.stringify(group[0].params);
    return group.every(request => JSON.stringify(request.params) === firstParams);
  }
}

// Global singleton for batching
const staticParamsBatcher = new StaticParamsBatcher();

/**
 * Batched version of getStories for use in generateStaticParams
 */
export const batchedGetStories = <T>(params: GetStoriesParams) => 
  staticParamsBatcher.batchGetStories<T>(params);

/**
 * Sequential processing for multiple static param generations
 */
export async function generateStaticParamsSequentially<T>(
  generators: Array<() => Promise<T>>,
  delayBetweenGenerators = 200
): Promise<T[]> {
  const results: T[] = [];
  
  for (let i = 0; i < generators.length; i++) {
    const generator = generators[i];
    const result = await generator();
    results.push(result);
    
    // Add delay between generators if not the last one
    if (i < generators.length - 1) {
      await new Promise(resolve => setTimeout(resolve, delayBetweenGenerators));
    }
  }
  
  return results;
}

/**
 * Optimized version of generateStaticParamsByType with batching
 */
export async function generateStaticParamsByTypeOptimized(
  componentType: string,
  locales: string[] = ['en', 'ru'],
  delayBetweenLocales = 150
): Promise<Array<{ locale: string; slug: string }>> {
  const allParams: Array<{ locale: string; slug: string }> = [];
  
  for (const locale of locales) {
    try {
      const { data } = await batchedGetStories({
        starts_with: `immigrantinvest/${locale}`,
        filter_query: {
          component: { in: componentType },
        },
        per_page: 100,
        version: 'published',
      });
      
      const localeParams = data.stories.map(story => ({
        locale,
        slug: story.slug,
      }));
      
      allParams.push(...localeParams);
      
      // Add delay between locales
      if (locales.indexOf(locale) < locales.length - 1) {
        await new Promise(resolve => setTimeout(resolve, delayBetweenLocales));
      }
    } catch (error) {
      console.warn(`Failed to generate static params for ${componentType} in locale ${locale}:`, error);
    }
  }
  
  return allParams;
}

export default staticParamsBatcher;
